/**
 * Tiny relative-time formatters shared across the portal.
 *
 * Two surface variants intentionally — different surfaces want different
 * forms of the same underlying calculation, so the helper centralises
 * the math + thresholds and the caller picks the cosmetic shape:
 *
 *   formatRelativeShort('2026-05-11T...') → '5m' | '3h' | '2d' | '4w' | 'May 4'
 *   formatRelativeLong ('2026-05-11T...') → '5m ago' | 'Never' | 'May 4'
 *
 * The `Never` sentinel only fires for the long form (the admin People
 * directory needs to render *something* in the "Last active" column for
 * a member who never signed in); the short form returns '' for invalid
 * or absent input so the topbar bell renders no timestamp at all.
 *
 * `isRecent` is the live/idle predicate the People directory uses to
 * pick the Status pill colour — pulled into the same module so the
 * "what counts as recent" threshold is co-located with the formatter.
 */

const MIN_MS = 60_000;
const HOUR_MS = 3_600_000;
const RECENT_THRESHOLD_MS = 24 * HOUR_MS;

function parseIso(iso: string | undefined): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  return Number.isFinite(at) ? at : null;
}

/**
 * Compact relative time — no "ago" suffix, adds weeks tier.
 * Returns '' for missing or invalid input (callers render nothing).
 *
 * Used by topbar notification bell + right-rail signals strip — the
 * row already has the source label so the timestamp is decorative.
 */
export function formatRelativeShort(iso: string | undefined): string {
  const at = parseIso(iso);
  if (at === null) return '';
  const diffMs = Date.now() - at;
  const minutes = Math.floor(diffMs / MIN_MS);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Long-form relative time — explicit "ago" suffix, with a "Never"
 * sentinel for missing input so columns like "Last active" render
 * something semantically meaningful.
 *
 * Used by the admin People directory's last-active column where the
 * cell needs prose, not just a delta.
 */
export function formatRelativeLong(iso: string | undefined): string {
  const at = parseIso(iso);
  if (at === null) return 'Never';
  const diffMs = Date.now() - at;
  const minutes = Math.floor(diffMs / MIN_MS);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * "Was this within the last 24h?" predicate.
 * Used by the admin People directory to pick the Status pill kind
 * (live ↔ idle) without a second date-parse round-trip.
 */
export function isRecent(iso: string | undefined, withinMs: number = RECENT_THRESHOLD_MS): boolean {
  const at = parseIso(iso);
  if (at === null) return false;
  return Date.now() - at < withinMs;
}

/**
 * Same long-form output, different sentinels — `'just now'` for sub-
 * minute and `'—'` (em-dash) for missing/invalid input. Used by /ask's
 * "Threaded with N events · streamed Xs ago" page-head where "Never"
 * would read awkwardly. Shares the same minute/hour/day/week tiers as
 * `formatRelativeLong`.
 */
export function formatRelativeAgo(iso: string | undefined): string {
  const at = parseIso(iso);
  if (at === null) return '—';
  const diffMs = Date.now() - at;
  const minutes = Math.floor(diffMs / MIN_MS);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
